import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { getAgencyId } from '@app/selectors/agency';
import useToast from '@app/hooks/useToast';
import { ToastTypes } from '@app/common/components/Toast';
import { getErrorMessageForToast } from '@app/utils/errorUtils';
import { updateAgencyInfo } from '@app/actions/account';
import { updateHasRequestedEocAccess } from '@app/actions/user';
import { Agency } from '@app/types/Agency';
import { User } from '@app/types/User';

export const useEocAccessApi = () => {
    const agencyId = useSelector(getAgencyId);
    const dispatch = useDispatch();
    const { addNewToast } = useToast();

    const requestEocAccessMutation = useMutation<{ agency: Agency; agent: User }>(
        async () => {
            const response = await axios.post(`/api/agency/${agencyId}/request-eoc-feature`);
            return response.data;
        },
        {
            onError: error => {
                window.rollbar.error('Error requesting EOC access', error);
                addNewToast(
                    getErrorMessageForToast('We were unable to request Exchange of Contracts access.'),
                    ToastTypes.ERROR
                );
            },
            onSuccess: ({ agent }) => {
                dispatch(updateHasRequestedEocAccess(agent.hasRequestedEocAccess));
                addNewToast('Your Exchange of Contracts access request has been submitted.', ToastTypes.SUCCESS);
            }
        }
    );

    const enableEocAccessMutation = useMutation(
        async () => {
            const response = await axios.post(`/api/agency/${agencyId}/enable-eoc-feature`);
            return response.data;
        },
        {
            onError: error => {
                window.rollbar.error('Error enabling EOC access', error);
                addNewToast(getErrorMessageForToast('We were unable to enable EOC access.'), ToastTypes.ERROR);
            },
            onSuccess: data => {
                dispatch(updateAgencyInfo(data));
                addNewToast('Exchange of Contracts access has been enabled for your agency.', ToastTypes.SUCCESS);
            }
        }
    );

    const disableEocAccessMutation = useMutation(
        async () => {
            const response = await axios.post(`/api/agency/${agencyId}/disable-eoc-feature`);
            return response.data;
        },
        {
            onError: error => {
                window.rollbar.error('Error disabling EOC access', error);
                addNewToast(getErrorMessageForToast('We were unable to disable EOC access.'), ToastTypes.ERROR);
            },
            onSuccess: data => {
                dispatch(updateAgencyInfo(data));
                addNewToast('Exchange of Contracts access has been disabled for your agency.', ToastTypes.SUCCESS);
            }
        }
    );

    return {
        requestEocAccessMutation,
        enableEocAccessMutation,
        disableEocAccessMutation,
        // Loading states for better UX
        isRequestingAccess: requestEocAccessMutation.isLoading,
        isEnablingAccess: enableEocAccessMutation.isLoading,
        isDisablingAccess: disableEocAccessMutation.isLoading,
        isAnyOperationPending:
            requestEocAccessMutation.isLoading ||
            enableEocAccessMutation.isLoading ||
            disableEocAccessMutation.isLoading
    };
};
